import { useLayoutEffect, useRef, useState } from "react";

/** Default bottom content padding (in px) used in dynamic height calculations. */
const DEFAULT_DYNAMIC_HEIGHT_CONTENT_PADDING = 30;

/** Default container border size (in px) used in dynamic height calculations. */
const DEFAULT_DYNAMIC_HEIGHT_BORDER = 2;

/** Combined default bottom offset (padding + border, in px) for dynamic height. */
const DEFAULT_DYNAMIC_HEIGHT_BOTTOM_OFFSET = DEFAULT_DYNAMIC_HEIGHT_CONTENT_PADDING + DEFAULT_DYNAMIC_HEIGHT_BORDER;
/**
 * Dynamically calculates the height a container should occupy to fill the remaining
 * viewport space below its current top position.
 */
export function useDynamicHeight({
  shouldSkip = false,
  deps = []
} = {}) {
  const ref = useRef(null);
  const [height, setHeight] = useState();
  useLayoutEffect(() => {
    if (!ref.current || shouldSkip) {
      return undefined;
    }
    function updateHeight() {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      // Skip while hidden: a display:none element collapses its rect to zeros,
      // and baking that would leave a bogus height once it is shown again.
      if (rect.width === 0 && rect.height === 0) {
        return;
      }
      setHeight(`calc(100dvh - (${rect.top}px + ${DEFAULT_DYNAMIC_HEIGHT_BOTTOM_OFFSET}px))`);
    }

    // Run after layout, and schedule again on the next frame to catch late DOM updates (e.g., banners)
    const requestAnimationFrameId = requestAnimationFrame(updateHeight);
    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(document.body);
    // Content above shrinking or growing, e.g. a collapsed panel, resizes the ancestors but not the body
    for (let ancestor = ref.current.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
      resizeObserver.observe(ancestor);
    }
    window.addEventListener("resize", updateHeight);

    // Recalculate immediately to cover state-driven layout changes above the container
    updateHeight();
    return () => {
      cancelAnimationFrame(requestAnimationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
    // `deps` is intentionally spread so consumers can opt into recalculation triggers.
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSkip, ...deps]);
  return {
    ref,
    height
  };
}